// src/v3/ui/scheduler/cron-schedule-form.tsx

import { useEffect, useState } from "react";
import { Button, formatDisplayDateTime, Select } from "../_common";
import { t, type TranslationKey } from "../locales";
import {
  CRON_FREQUENCIES,
  CRON_FREQUENCY_OBJ,
  describeCron,
  formToCron,
  getNextRun,
  parseCronToForm,
  validateCron,
} from "./cron-form.ui";

interface CronScheduleFormProps {
  cronExpression: string;
  onSave: (expr: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
  onClearMessages: () => void;
}

const DAYS_OF_WEEK: Array<{ value: string; labelKey: TranslationKey }> = [
  { value: "0", labelKey: "scheduler.day.sunday" },
  { value: "1", labelKey: "scheduler.day.monday" },
  { value: "2", labelKey: "scheduler.day.tuesday" },
  { value: "3", labelKey: "scheduler.day.wednesday" },
  { value: "4", labelKey: "scheduler.day.thursday" },
  { value: "5", labelKey: "scheduler.day.friday" },
  { value: "6", labelKey: "scheduler.day.saturday" },
];
export function CronScheduleForm({
  cronExpression,
  onSave,
  isLoading,
  error,
  successMessage,
  onClearMessages,
}: CronScheduleFormProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => parseCronToForm(cronExpression));
  const [customExpr, setCustomExpr] = useState(cronExpression);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setForm(parseCronToForm(cronExpression));
    setCustomExpr(cronExpression);
  }, [cronExpression]);

  const currentExpr =
    form.frequency === CRON_FREQUENCY_OBJ.CUSTOM
      ? customExpr
      : formToCron(form.frequency, form.minute, form.hour, form.dayOfWeek);

  const humanReadable = describeCron(currentExpr);
  const nextRun = getNextRun(currentExpr);

  const handleSave = async () => {
    const expr = currentExpr;
    if (!validateCron(expr)) {
      setValidationError("Invalid cron expression");
      return;
    }
    setValidationError(null);
    await onSave(expr);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setForm(parseCronToForm(cronExpression));
    setCustomExpr(cronExpression);
    setValidationError(null);
    onClearMessages();
    setIsEditing(false);
  };

  return (
    <div className="space-y-3">
      {!isEditing && (
        <div className="space-y-2">
          <div className="text-sm font-medium">{humanReadable}</div>
          {nextRun && (
            <div className="text-xs text-muted-foreground">
              {t("scheduler.nextRun")}: {formatDisplayDateTime(nextRun)}
            </div>
          )}
          <div className="text-xs font-mono text-muted-foreground">
            {cronExpression}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              onClearMessages();
              setIsEditing(true);
            }}
            disabled={isLoading}
          >
            {t("scheduler.edit")}
          </Button>
        </div>
      )}

      {isEditing && (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">
              {t("scheduler.frequency")}
            </label>
            <Select
              value={form.frequency}
              onChange={(value) => {
                const frequency = CRON_FREQUENCIES.find(
                  (option) => option === value,
                );
                if (frequency) setForm({ ...form, frequency });
                onClearMessages();
              }}
              options={CRON_FREQUENCIES.map((frequency) => ({
                value: frequency,
                label:
                  frequency === CRON_FREQUENCY_OBJ.DAILY
                    ? t("scheduler.frequencyDaily")
                    : frequency === CRON_FREQUENCY_OBJ.WEEKLY
                      ? t("scheduler.frequencyWeekly")
                      : t("scheduler.frequencyCustom"),
              }))}
              ariaLabel={t("scheduler.frequency")}
              className="mt-1"
            />
          </div>

          {form.frequency !== CRON_FREQUENCY_OBJ.CUSTOM && (
            <div className="flex gap-2">
              <div>
                <label className="text-xs text-muted-foreground">
                  {t("scheduler.hour")}
                </label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={form.hour}
                  onChange={(e) => {
                    setForm({ ...form, hour: e.target.value });
                    onClearMessages();
                  }}
                  className="mt-1 block w-20 rounded-md border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">
                  {t("scheduler.minute")}
                </label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={form.minute}
                  onChange={(e) => {
                    setForm({ ...form, minute: e.target.value });
                    onClearMessages();
                  }}
                  className="mt-1 block w-20 rounded-md border px-3 py-2 text-sm"
                />
              </div>
              {form.frequency === CRON_FREQUENCY_OBJ.WEEKLY && (
                <div>
                  <label className="text-xs text-muted-foreground">
                    {t("scheduler.dayOfWeek")}
                  </label>
                  <Select
                    value={form.dayOfWeek}
                    onChange={(dayOfWeek) => {
                      setForm({ ...form, dayOfWeek });
                      onClearMessages();
                    }}
                    options={DAYS_OF_WEEK.map((d) => ({
                      value: d.value,
                      label: t(d.labelKey),
                    }))}
                    ariaLabel={t("scheduler.dayOfWeek")}
                    className="mt-1 w-32"
                  />
                </div>
              )}
            </div>
          )}

          {form.frequency === CRON_FREQUENCY_OBJ.CUSTOM && (
            <div>
              <label className="text-xs text-muted-foreground">
                {t("scheduler.cronExpression")}
              </label>
              <input
                type="text"
                value={customExpr}
                onChange={(e) => {
                  setCustomExpr(e.target.value);
                  onClearMessages();
                }}
                placeholder="0 5 * * *"
                className="mt-1 block w-full rounded-md border px-3 py-2 font-mono text-sm"
              />
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            {t("scheduler.preview")}: {humanReadable}
          </div>

          {validationError && (
            <p className="text-xs text-red-500">{validationError}</p>
          )}
          {error && <p className="text-xs text-red-500">{error}</p>}
          {successMessage && (
            <p className="text-xs text-green-600">{successMessage}</p>
          )}

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => void handleSave()}
              disabled={isLoading}
            >
              {t("scheduler.save")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              {t("scheduler.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
